import {
    ActionMenu,
    Card,
    Colors,
    ConfirmDialog,
    FOCUS_RING,
    formatDate,
    Heading,
    Icon,
    Pills,
    subtleHover,
    Text,
    type IconName,
} from '@amelio/ui-library';
import { useState } from 'react';

import { DeleteObjectivePeriodDialog } from './DeleteObjectivePeriodDialog';

import type { ObjectivePeriodStatusType } from '@/constants/ObjectivePeriodStatus';
import type { TranslationKey } from '@/i18n-key';
import type { ObjectivePeriod } from '@/types/objective-periods';
import type { OptionProps } from '@amelio/ui-library';

import { useSetObjectivePeriodStatus } from '@/api/objective-periods';
import {
    isObjectivePeriodCompleted,
    ObjectivePeriodStatus,
} from '@/constants/ObjectivePeriodStatus';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import i18n from '@/plugins/i18n';
import { showSuccessToast } from '@/plugins/Toast';
import {
    cycleCardRadius,
    cycleRowCardSpacing,
    cycleRowIconFrame,
    cycleRowIconSize,
} from '@/utils/cycleStyles';
import { getLocalizedText } from '@/utils/localization';
import {
    getObjectivePeriodPillStyle,
    getObjectivePeriodRowIconColor,
    getObjectivePeriodStatusConfirmation,
    getObjectivePeriodStatusLabelKey,
} from '@/utils/objectivePeriodStatus';

const ROW_ICON = 'time-calendar';

/** The past participle each status change is announced with ("{{name}} a été désactivée."). */
const STATUS_ACTION_KEYS: Readonly<Partial<Record<ObjectivePeriodStatusType, TranslationKey>>> = {
    [ObjectivePeriodStatus.ACTIVE]: 'objectivePeriods.activated',
    [ObjectivePeriodStatus.DEACTIVATED]: 'objectivePeriods.deactivated',
    [ObjectivePeriodStatus.COMPLETED]: 'objectivePeriods.completed',
};

export interface ObjectivePeriodRowProps {
    period: ObjectivePeriod;
    onEdit: () => void;
}

export const ObjectivePeriodRow = ({ period, onEdit }: ObjectivePeriodRowProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const setStatusMutation = useSetObjectivePeriodStatus();

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<ObjectivePeriodStatusType | null>(null);
    const pendingConfirmation = pendingStatus
        ? getObjectivePeriodStatusConfirmation(pendingStatus)
        : undefined;

    const displayName = getLocalizedText(period.name);
    const isCompleted = isObjectivePeriodCompleted(period.status);
    const pillStyle = getObjectivePeriodPillStyle(period.status);
    const dateRange = t('objectivePeriods.dateRange', {
        start: formatDate({ date: period.startDate, language: i18n.language }),
        end: formatDate({ date: period.endDate, language: i18n.language }),
    });

    const runStatusChange = async (status: ObjectivePeriodStatusType) => {
        try {
            await setStatusMutation.mutateAsync({ id: period.id, status });
            const actionKey = STATUS_ACTION_KEYS[status];
            showSuccessToast({
                message: t('objectivePeriods.statusChangeSuccess', {
                    name: displayName,
                    action: actionKey ? t(actionKey) : status,
                }),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const requestStatusChange = (status: ObjectivePeriodStatusType) => {
        if (getObjectivePeriodStatusConfirmation(status)) {
            setPendingStatus(status);
            return;
        }

        runStatusChange(status);
    };

    const handleConfirmStatusChange = async () => {
        if (!pendingStatus) return;

        await runStatusChange(pendingStatus);
        setPendingStatus(null);
    };

    const menuItem = (
        id: string,
        labelKey: TranslationKey,
        iconName: IconName,
        onClick: () => void,
        danger = false,
    ): OptionProps => ({
        id,
        label: t(labelKey),
        onClick,
        danger,
        icon: (
            <Icon
                name={iconName}
                size={'sm'}
                color={'inherit'}
            />
        ),
    });

    const deleteItem = menuItem(
        'delete',
        'objectivePeriods.delete',
        'essential-trash',
        () => setIsDeleteDialogOpen(true),
        true,
    );

    const statusItems =
        period.status === ObjectivePeriodStatus.ACTIVE
            ? [
                  menuItem('deactivate', 'objectivePeriods.deactivate', 'essential-slash', () =>
                      requestStatusChange(ObjectivePeriodStatus.DEACTIVATED),
                  ),
                  menuItem('complete', 'objectivePeriods.complete', 'essential-tick-circle', () =>
                      requestStatusChange(ObjectivePeriodStatus.COMPLETED),
                  ),
              ]
            : [
                  menuItem('activate', 'objectivePeriods.activate', 'essential-checkmark', () =>
                      requestStatusChange(ObjectivePeriodStatus.ACTIVE),
                  ),
              ];

    // A completed period is frozen server-side: editing it and every status change are refused,
    // so its row offers deletion alone rather than menu entries that would answer 409.
    const menuItems: OptionProps[] = isCompleted
        ? [deleteItem]
        : [
              menuItem('edit', 'objectivePeriods.edit', 'edit-edit-2', onEdit),
              ...statusItems,
              deleteItem,
          ];

    // A click anywhere on the row opens the edit modal; the keyboard and assistive technology reach
    // it through the button on the name, so the row never wraps the kebab menu inside a control of
    // its own. A completed period has nothing to edit: no click, no button, no hover affordance.
    const interactiveClasses = isCompleted ? '' : `cursor-pointer ${subtleHover}`;

    return (
        <>
            <div
                onClick={isCompleted ? undefined : onEdit}
                className={`relative flex w-full items-center has-[button[aria-expanded="true"]]:z-50 ${interactiveClasses}`}
                data-testid={`objective-period-row-${period.id}`}
            >
                <Card
                    variant={'flat'}
                    padding={'none'}
                    className={`flex w-full flex-wrap justify-between border max-md:p-[16px] md:flex-row md:items-center md:p-[24px] ${cycleRowCardSpacing} ${cycleCardRadius}`}
                    borderColor={'white'}
                >
                    <div className={'flex flex-3 items-center'}>
                        <div
                            className={`bg-base-50 mr-[12px] flex shrink-0 items-center justify-center self-center rounded-lg ${cycleRowIconFrame}`}
                            aria-hidden={'true'}
                        >
                            <Icon
                                name={ROW_ICON}
                                className={cycleRowIconSize}
                                color={getObjectivePeriodRowIconColor(period.status)}
                            />
                        </div>

                        <div className={'flex min-w-0 flex-col'}>
                            <Heading
                                level={5}
                                className={'wrap-break-word'}
                            >
                                {isCompleted ? (
                                    displayName
                                ) : (
                                    <button
                                        type={'button'}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onEdit();
                                        }}
                                        className={`${FOCUS_RING} text-left`}
                                        data-testid={`objective-period-name-${period.id}`}
                                    >
                                        {displayName}
                                    </button>
                                )}
                            </Heading>

                            <Text
                                size={'small'}
                                color={Colors.BASE_400}
                            >
                                {dateRange}
                            </Text>
                        </div>
                    </div>

                    <div
                        className={
                            'flex flex-1 items-center justify-end gap-[12px] max-md:mt-[16px] max-md:w-full'
                        }
                    >
                        <Pills
                            variant={pillStyle.variant}
                            status={pillStyle.status}
                            size={'sm'}
                            className={pillStyle.className}
                        >
                            {t(getObjectivePeriodStatusLabelKey(period.status))}
                        </Pills>

                        <div onClick={(event) => event.stopPropagation()}>
                            <ActionMenu
                                items={menuItems}
                                ariaLabel={t('objectivePeriods.moreOptions')}
                                variant={'subtle'}
                                size={'sm'}
                                className={'!rounded-md border-gray-300 bg-white'}
                            />
                        </div>
                    </div>
                </Card>
            </div>

            {pendingConfirmation && (
                <ConfirmDialog
                    isOpen
                    title={t(pendingConfirmation.title)}
                    message={t(pendingConfirmation.message, { name: displayName })}
                    confirmLabel={t(pendingConfirmation.confirmLabel)}
                    cancelLabel={t('objectivePeriods.cancel')}
                    onConfirm={handleConfirmStatusChange}
                    onCancel={() => setPendingStatus(null)}
                    isLoading={setStatusMutation.isPending}
                    isDestructive={pendingStatus === ObjectivePeriodStatus.COMPLETED}
                    dataTestId={'objective-period-status-dialog'}
                />
            )}

            <DeleteObjectivePeriodDialog
                isOpen={isDeleteDialogOpen}
                period={period}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onSuccess={() => setIsDeleteDialogOpen(false)}
            />
        </>
    );
};
